import { useState, useEffect } from 'react'
import { FileText, Edit, Save, X, Eye, Globe, ChevronDown } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'

interface Page {
    id: number
    slug: string
    title_en: string
    title_vi: string
    content_en: string
    content_vi: string
    meta_description_en: string
    meta_description_vi: string
    meta_keywords: string
    status: string
    updated_at: string
}

export default function PagesManagement() {
    const { language } = useLanguageStore()
    const [pages, setPages] = useState<Page[]>([])
    const [loading, setLoading] = useState(true)
    const [editingPage, setEditingPage] = useState<Page | null>(null)
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState<'en' | 'vi'>('en')
    const [showSeo, setShowSeo] = useState(false)

    useEffect(() => {
        fetchPages()
    }, [])

    const fetchPages = async () => {
        try {
            const token = localStorage.getItem('auth_token')
            const response = await fetch('/api/admin/pages', {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            const data = await response.json()
            if (data.success && data.data) {
                setPages(data.data)
            }
        } catch (error) {
            console.error('Error fetching pages:', error)
        } finally {
            setLoading(false)
        }
    }

    const loadPageForEdit = async (pageId: number) => {
        try {
            const token = localStorage.getItem('auth_token')
            const response = await fetch(`/api/admin/pages/${pageId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            const data = await response.json()
            if (data.success && data.data) {
                setEditingPage(data.data)
            }
        } catch (error) {
            console.error('Error loading page:', error)
        }
    }

    const savePage = async () => {
        if (!editingPage) return
        setSaving(true)

        try {
            const token = localStorage.getItem('auth_token')
            const response = await fetch('/api/admin/pages', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingPage)
            })
            const data = await response.json()

            if (data.success) {
                setEditingPage(null)
                fetchPages()
                alert(language === 'en' ? 'Page saved successfully!' : 'Lưu trang thành công!')
            } else {
                alert(data.error || 'Error saving page')
            }
        } catch (error) {
            console.error('Error saving page:', error)
            alert('Error saving page')
        } finally {
            setSaving(false)
        }
    }

    const getPageTitle = (slug: string) => {
        const titles: Record<string, { en: string, vi: string }> = {
            about: { en: 'About Us', vi: 'Về chúng tôi' },
            contact: { en: 'Contact', vi: 'Liên hệ' },
            terms: { en: 'Terms of Service', vi: 'Điều khoản' },
            privacy: { en: 'Privacy Policy', vi: 'Bảo mật' },
            faq: { en: 'FAQ', vi: 'Hỏi đáp' }
        }
        return titles[slug] || { en: slug, vi: slug }
    }

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center">
                <div className="animate-pulse text-gray-500">
                    {language === 'en' ? 'Loading pages...' : 'Đang tải...'}
                </div>
            </div>
        )
    }

    // Edit Modal
    if (editingPage) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setEditingPage(null)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h1 className="text-2xl font-bold">
                            {language === 'en' ? 'Edit Page' : 'Chỉnh sửa trang'}: {editingPage.slug}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href={`/${editingPage.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            {language === 'en' ? 'Preview' : 'Xem trước'}
                        </a>
                        <button
                            onClick={savePage}
                            disabled={saving}
                            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? (language === 'en' ? 'Saving...' : 'Đang lưu...') : (language === 'en' ? 'Save' : 'Lưu')}
                        </button>
                    </div>
                </div>

                {/* Language Tabs */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('en')}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${activeTab === 'en' ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                    >
                        <Globe className="w-4 h-4" />
                        English
                    </button>
                    <button
                        onClick={() => setActiveTab('vi')}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${activeTab === 'vi' ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                    >
                        <Globe className="w-4 h-4" />
                        Tiếng Việt
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    {/* Title */}
                    <div className="bg-white rounded-xl border p-6">
                        <label className="block font-semibold mb-2">
                            {language === 'en' ? 'Page Title' : 'Tiêu đề trang'} ({activeTab === 'en' ? 'English' : 'Tiếng Việt'})
                        </label>
                        <input
                            type="text"
                            value={activeTab === 'en' ? editingPage.title_en : editingPage.title_vi || ''}
                            onChange={(e) => setEditingPage({
                                ...editingPage,
                                [activeTab === 'en' ? 'title_en' : 'title_vi']: e.target.value
                            })}
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            placeholder={language === 'en' ? 'Enter title...' : 'Nhập tiêu đề...'}
                        />
                    </div>

                    {/* Content Editor */}
                    <div className="bg-white rounded-xl border p-6">
                        <label className="block font-semibold mb-2">
                            {language === 'en' ? 'Page Content' : 'Nội dung trang'} ({activeTab === 'en' ? 'English' : 'Tiếng Việt'})
                        </label>
                        <p className="text-sm text-gray-500 mb-4">
                            {language === 'en'
                                ? 'Use HTML tags for formatting: <h2>, <h3>, <p>, <ul>, <li>, <strong>, <a href="">, etc.'
                                : 'Sử dụng thẻ HTML: <h2>, <h3>, <p>, <ul>, <li>, <strong>, <a href="">, v.v.'}
                        </p>
                        <textarea
                            value={activeTab === 'en' ? editingPage.content_en : editingPage.content_vi || ''}
                            onChange={(e) => setEditingPage({
                                ...editingPage,
                                [activeTab === 'en' ? 'content_en' : 'content_vi']: e.target.value
                            })}
                            rows={15}
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                            placeholder={language === 'en' ? 'Enter HTML content...' : 'Nhập nội dung HTML...'}
                        />
                    </div>

                    {/* SEO Section */}
                    <div className="bg-white rounded-xl border overflow-hidden">
                        <button
                            onClick={() => setShowSeo(!showSeo)}
                            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                        >
                            <span className="font-semibold">SEO Settings</span>
                            <ChevronDown className={`w-5 h-5 transition ${showSeo ? 'rotate-180' : ''}`} />
                        </button>

                        {showSeo && (
                            <div className="p-6 border-t space-y-4">
                                <div>
                                    <label className="block font-medium mb-2">
                                        Meta Description ({activeTab === 'en' ? 'English' : 'Tiếng Việt'})
                                    </label>
                                    <textarea
                                        value={activeTab === 'en' ? editingPage.meta_description_en : editingPage.meta_description_vi || ''}
                                        onChange={(e) => setEditingPage({
                                            ...editingPage,
                                            [activeTab === 'en' ? 'meta_description_en' : 'meta_description_vi']: e.target.value
                                        })}
                                        rows={3}
                                        maxLength={160}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                        placeholder="Brief description for search engines (max 160 chars)"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {(activeTab === 'en' ? editingPage.meta_description_en : editingPage.meta_description_vi || '').length}/160
                                    </p>
                                </div>

                                <div>
                                    <label className="block font-medium mb-2">Meta Keywords</label>
                                    <input
                                        type="text"
                                        value={editingPage.meta_keywords || ''}
                                        onChange={(e) => setEditingPage({
                                            ...editingPage,
                                            meta_keywords: e.target.value
                                        })}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                        placeholder="keyword1, keyword2, keyword3"
                                    />
                                </div>

                                <div>
                                    <label className="block font-medium mb-2">Status</label>
                                    <select
                                        value={editingPage.status}
                                        onChange={(e) => setEditingPage({
                                            ...editingPage,
                                            status: e.target.value
                                        })}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="published">Published</option>
                                        <option value="draft">Draft</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // Pages List
    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">{language === 'en' ? 'Pages Management' : 'Quản Lý Trang'}</h1>
                    <p className="text-gray-600 mt-1">
                        {language === 'en' ? 'Edit website pages content' : 'Chỉnh sửa nội dung các trang'}
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Page' : 'Trang'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    URL
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    Status
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Last Updated' : 'Cập nhật'}
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Actions' : 'Thao tác'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {pages.map((page) => {
                                const titles = getPageTitle(page.slug)
                                return (
                                    <tr key={page.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <FileText className="w-5 h-5 text-primary-600" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold">{language === 'en' ? titles.en : titles.vi}</p>
                                                    <p className="text-sm text-gray-500">{page.title_en}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <a
                                                href={`/${page.slug}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary-600 hover:underline"
                                            >
                                                /{page.slug}
                                            </a>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${page.status === 'published'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {page.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {page.updated_at?.split('T')[0] || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => loadPageForEdit(page.id)}
                                                className="px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg inline-flex items-center gap-2"
                                            >
                                                <Edit className="w-4 h-4" />
                                                {language === 'en' ? 'Edit' : 'Sửa'}
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
